import Navbar from '@/components/Navbar';
import HeroSection from '@/components/HeroSection';
import PressStrip from '@/components/PressStrip';
import StatsSection from '@/components/StatsSection';
import ServicesSection from '@/components/ServicesSection';
import AboutSection from '@/components/AboutSection';
import WhyChooseUs from '@/components/WhyChooseUs';
import TestimonialsSection from '@/components/TestimonialsSection';
import LocationsSection from '@/components/LocationsSection';
import FaqSection from '@/components/FaqSection';
import ConsultationSection from '@/components/ConsultationSection';
import Footer from '@/components/Footer';
import FloatingCta from '@/components/FloatingCta';
import SchemaJsonLd from '@/components/SchemaJsonLd';
import { faqs } from '@/components/FaqSection';
import { pageDates } from '@/data/pageDates';

const homepageSchema = [
  {
    '@context': 'https://schema.org',
    '@type': ['LegalService', 'LocalBusiness'],
    '@id': 'https://www.morganlegalny.com/#organization',
    name: 'Morgan Legal Group, P.C.',
    alternateName: 'Morgan Legal Group',
    url: 'https://www.morganlegalny.com',
    logo: {
      '@type': 'ImageObject',
      '@id': 'https://www.morganlegalny.com/#logo',
      url: 'https://www.morganlegalny.com/brand/morgan-logo.png',
      contentUrl: 'https://www.morganlegalny.com/brand/morgan-logo.png',
      caption: 'Morgan Legal Group, P.C.',
    },
    image: 'https://www.morganlegalny.com/og-image-home.jpg',
    telephone: '+1-888-529-1315',
    email: 'contact@morganlegalny.com',
    foundingDate: '2017',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '15 Maiden Lane, Suite 905',
      addressLocality: 'New York',
      addressRegion: 'NY',
      postalCode: '10038',
      addressCountry: 'US',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 40.7089,
      longitude: -74.0078,
    },
    areaServed: [
      { '@type': 'City', name: 'New York' },
      { '@type': 'AdministrativeArea', name: 'Manhattan' },
      { '@type': 'AdministrativeArea', name: 'Brooklyn' },
      { '@type': 'AdministrativeArea', name: 'Queens' },
      { '@type': 'AdministrativeArea', name: 'Bronx' },
      { '@type': 'AdministrativeArea', name: 'Staten Island' },
      { '@type': 'AdministrativeArea', name: 'Long Island' },
    ],
    priceRange: '$$$',
    // 🔴 Часы приведены к тому, что фирма объявляет в СВОЁМ профиле Google
    // («Open 24 hours» у обоих листингов) и что подтвердил владелец 03.09:
    // звонок принимают круглосуточно операторы. Прежние 09:00–18:00 здесь
    // противоречили и профилю, и страницам локаций, где стояло 24/7, — сайт
    // спорил сам с собой на двух машиночитаемых слоях.
    // Приём У ЮРИСТА (Пн–Пт 9–18) — отдельная величина, и она сказана словами
    // в шапке, на главной и на каждой странице локации. Смешивать их в
    // openingHours нельзя: это поле про доступность бизнеса, а не про календарь
    // конкретного адвоката.
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
        opens: '00:00',
        closes: '23:59',
      },
    ],
    founder: {
      '@type': 'Person',
      '@id': 'https://www.morganlegalny.com/russel-morgan-esq/#person',
      name: 'Russel Morgan',
      jobTitle: 'Founder & Principal Attorney',
      image: 'https://www.morganlegalny.com/img/attorneys/russel-morgan-headshot.jpg',
      worksFor: { '@id': 'https://www.morganlegalny.com/#organization' },
    },
    knowsLanguage: ['English', 'Spanish', 'Russian'],
    // aggregateRating снят намеренно. Разметка отзывов о самом себе,
    // размещённая самим бизнесом на своей странице, запрещена правилами
    // Google для локального бизнеса: рейтинг обязан приходить с внешней
    // площадки. На проде такой узел есть — переезд его не наследует.
    // Видимые упоминания рейтинга в тексте это не затрагивает.
    // Verbatim from the production WordPress graph. The previous two-item list
    // pointed at a Facebook handle the firm does not use, and dropped the
    // Wikidata entity — the strongest identity link Google has for the firm.
    sameAs: [
      'https://www.facebook.com/morganlegalgroupPC',
      'https://x.com/MorganLegalNY',
      'https://www.instagram.com/morganlegalny/',
      'https://www.linkedin.com/company/morgan-legal-group/',
      'https://www.youtube.com/channel/UC7Eni2PtByck6fjyjITpMOA',
      'https://www.youtube.com/playlist?list=PL7uIi1SpUUgHqaI5DtRgK78uetmQcHp8W',
      'https://nearmelawyers.com/lawyer/new-york/new-york-city/morgan-legal-group-pc/22730/',
      'https://www.wikidata.org/wiki/Q140299024',
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': 'https://www.morganlegalny.com/#webpage',
    url: 'https://www.morganlegalny.com/',
    name: 'NYC Estate Planning & Probate | Free Consultation | Morgan Legal Group',
    isPartOf: { '@id': 'https://www.morganlegalny.com/#website' },
    about: { '@id': 'https://www.morganlegalny.com/#organization' },
    inLanguage: 'en-US',
    datePublished: pageDates('/').published,
    dateModified: pageDates('/').modified,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['h1', '#faq h2', '#faq h3'],
      xpath: ['/html/head/title', '/html/head/meta[@name="description"]/@content'],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': 'https://www.morganlegalny.com/#website',
    url: 'https://www.morganlegalny.com',
    name: 'Morgan Legal Group, P.C.',
    publisher: { '@id': 'https://www.morganlegalny.com/#organization' },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.morganlegalny.com/' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    '@id': 'https://www.morganlegalny.com/#faq',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  },
];

export default function Home() {
  return (
    <div className="min-h-screen bg-cream">
      <SchemaJsonLd schema={homepageSchema} />
      <Navbar />
      <main id="home">
        <HeroSection />
        <PressStrip />
        {/* Пометка обязана стоять на главной. В подвале она есть на всех страницах,
            но там растворяется в сплошном тексте — здесь отдельной строкой сразу под
            первым экраном. */}
        <div className="bg-cream border-b border-navy/10 py-2.5 text-center">
          <span className="font-body text-navy/45 text-[11px] uppercase tracking-[0.2em]">
            Attorney Advertising
          </span>
        </div>
        <StatsSection />
        <ServicesSection />
        <AboutSection />
        <WhyChooseUs />
        <TestimonialsSection />
        <LocationsSection />
        <FaqSection />
        <ConsultationSection />
      </main>
      <Footer />
      <FloatingCta />
    </div>
  );
}
